import React, { useEffect, useState } from 'react';
import { useStrategyOperations } from '../hooks/useStrategyOperations';
import { SignalAction } from '../types';
import config from '../config';
import SimpleRatioChart from '../components/SimpleRatioChart';
import PortfolioManager from '../components/portfolio/PortfolioManager';

const SimpleDashboard: React.FC = () => {
  const { signals, loading, error, getLatestSignals } = useStrategyOperations();
  const [activeTab, setActiveTab] = useState<string>('summary');
  const [selectedStock, setSelectedStock] = useState<string | null>(null);

  useEffect(() => {
    // Log to help with debugging
    console.log('Dashboard: Fetching trading signals...');
    console.log('Using real API:', config.useRealApi, 'Mock API:', config.useMockApi);
    
    // Force a refresh of the signals when the dashboard loads
    // Using type assertion to bypass TypeScript error
    getLatestSignals(true as any).then(() => {
      console.log('Dashboard: Signals fetched successfully');
    });
  }, [getLatestSignals]);

  // Log signals for debugging
  useEffect(() => {
    console.log('Current signals:', signals);
  }, [signals]);

  // Count signals by type
  const buySignals = signals.filter(s => s.finalSignal?.toUpperCase() === 'BUY').length;
  const sellSignals = signals.filter(s => s.finalSignal?.toUpperCase() === 'SELL').length;
  const holdSignals = signals.filter(s => s.finalSignal?.toUpperCase() === 'HOLD').length;

  // Get all buy and sell signals
  const allBuySignals = signals
    .filter(s => s.finalSignal?.toUpperCase() === 'BUY')
    // Sort by Triple Point strength: first by confidence, then by F-Score, then by CAPE
    .sort((a, b) => {
      // First compare by confidence (strength of signal)
      if ((b.confidence || 0) !== (a.confidence || 0)) {
        return (b.confidence || 0) - (a.confidence || 0);
      }
      // Then by F-Score (financial strength)
      const aFScore = typeof a.fScore === 'number' ? a.fScore : 
                     (a.fScore && typeof a.fScore === 'object' && 'score' in a.fScore) ? a.fScore.score : 
                     a.f_score || 0;
      const bFScore = typeof b.fScore === 'number' ? b.fScore : 
                     (b.fScore && typeof b.fScore === 'object' && 'score' in b.fScore) ? b.fScore.score : 
                     b.f_score || 0;
      if (bFScore !== aFScore) {
        return bFScore - aFScore;
      }
      // Finally by CAPE (value - lower is better for buy)
      return (a.cape || 0) - (b.cape || 0);
    });

  const allSellSignals = signals
    .filter(s => s.finalSignal?.toUpperCase() === 'SELL')
    // Sort by Triple Point strength: first by confidence, then by F-Score (lower is better for sell), then by CAPE (higher is better for sell)
    .sort((a, b) => {
      // First compare by confidence (strength of signal)
      if ((b.confidence || 0) !== (a.confidence || 0)) {
        return (b.confidence || 0) - (a.confidence || 0);
      }
      // Then by F-Score (lower is better for sell signals)
      const aFScore = typeof a.fScore === 'number' ? a.fScore : 
                     (a.fScore && typeof a.fScore === 'object' && 'score' in a.fScore) ? a.fScore.score : 
                     a.f_score || 0;
      const bFScore = typeof b.fScore === 'number' ? b.fScore : 
                     (b.fScore && typeof b.fScore === 'object' && 'score' in b.fScore) ? b.fScore.score : 
                     b.f_score || 0;
      if (aFScore !== bFScore) {
        return aFScore - bFScore;
      }
      // Finally by CAPE (higher is better for sell)
      return (b.cape || 0) - (a.cape || 0);
    });

  const handleViewStock = (ticker: string) => {
    setSelectedStock(ticker);
    // You could expand this to navigate to a stock detail page or show a modal
    console.log(`Viewing stock details for: ${ticker}`);
  };

  const renderLoadingState = () => (
    <div className="text-center py-4">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-2 text-primary">Loading signals...</p>
    </div>
  );

  const renderErrorState = () => (
    <div className="alert alert-danger mt-4" role="alert">
      <h4 className="alert-heading">Error loading signals</h4>
      <p>{error}</p>
    </div>
  );

  // Render a signal badge with appropriate color
  const renderSignalBadge = (signal?: SignalAction) => {
    if (!signal) return null;
    
    const signalUpper = signal.toUpperCase();
    let className = '';
    
    switch (signalUpper) {
      case 'BUY':
        className = 'badge bg-success';
        break;
      case 'SELL':
        className = 'badge bg-danger';
        break;
      default:
        className = 'badge bg-secondary';
    }
    
    return <span className={className}>{signalUpper}</span>;
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="m-0">Dashboard</h1>
        <button 
          className="refresh-btn"
          onClick={() => getLatestSignals(true as any)}
          disabled={loading}
        >
          {loading ? (
            <>
              <span 
                className="spinner-border spinner-border-sm" 
                role="status" 
                aria-hidden="true"
              ></span>
              Refreshing...
            </>
          ) : (
            <>
              <i className="bi bi-arrow-clockwise"></i>
              Refresh Signals
            </>
          )}
        </button>
      </div>

      {error ? (
        renderErrorState()
      ) : (
        <div className="mb-4">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'summary' ? 'active' : ''}`}
                onClick={() => setActiveTab('summary')}
              >
                Summary
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'visualization' ? 'active' : ''}`}
                onClick={() => setActiveTab('visualization')}
              >
                CAPE Visualization
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'portfolio' ? 'active' : ''}`}
                onClick={() => setActiveTab('portfolio')}
              >
                Portfolio Management
              </button>
            </li>
          </ul>
          
          <div className="tab-content">
            {loading && !signals.length ? (
              renderLoadingState()
            ) : (
              <>
                {/* Summary Tab Content */}
                <div className={`tab-pane fade ${activeTab === 'summary' ? 'show active' : ''}`}>
                  {/* Signal summary cards */}
                  <div className="row mb-4">
                    {/* Buy Signal Card */}
                    <div className="col-md-4 mb-3">
                      <div className="card signal-card buy-card dashboard-card">
                        <div className="card-body">
                          <div className="signal-title text-success">
                            BUY SIGNALS
                          </div>
                          <div className="card-count">{buySignals}</div>
                          <div className="signal-icon text-success">
                            <i className="bi bi-arrow-up"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Sell Signal Card */}
                    <div className="col-md-4 mb-3">
                      <div className="card signal-card sell-card dashboard-card">
                        <div className="card-body">
                          <div className="signal-title text-danger">
                            SELL SIGNALS
                          </div>
                          <div className="card-count">{sellSignals}</div>
                          <div className="signal-icon text-danger">
                            <i className="bi bi-arrow-down"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Hold Signal Card */}
                    <div className="col-md-4 mb-3">
                      <div className="card signal-card hold-card dashboard-card">
                        <div className="card-body">
                          <div className="signal-title text-secondary">
                            HOLD SIGNALS
                          </div>
                          <div className="card-count">{holdSignals}</div>
                          <div className="signal-icon text-secondary">
                            <i className="bi bi-pause"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Signal tables */}
                  <div className="row">
                    {/* Buy Signals Table */}
                    <div className="col-md-6 mb-4">
                      <h6 className="dashboard-section-title">Top Buy Signals</h6>
                      <div className="card shadow dashboard-card">
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th>TICKER</th>
                                <th>CAPE VALUE</th>
                                <th>F-SCORE</th>
                                <th>SIGNAL</th>
                              </tr>
                            </thead>
                            <tbody>
                              {allBuySignals.length > 0 ? (
                                allBuySignals.map((signal) => (
                                  <tr key={signal.ticker}>
                                    <td>
                                      <span 
                                        className="text-primary stock-link"
                                        onClick={() => handleViewStock(signal.ticker)}
                                      >
                                        {signal.ticker}
                                      </span>
                                    </td>
                                    <td>{signal.cape !== undefined ? signal.cape.toFixed(2) : 'N/A'}</td>
                                    <td>
                                      {signal.fScore && typeof signal.fScore === 'object' && 'score' in signal.fScore 
                                        ? `${signal.fScore.score}/9` 
                                        : signal.f_score ? `${signal.f_score}/9` : 'N/A'}
                                    </td>
                                    <td>
                                      {renderSignalBadge(signal.finalSignal as SignalAction)}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={4} className="text-center">No buy signals available</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    
                    {/* Sell Signals Table */}
                    <div className="col-md-6 mb-4">
                      <h6 className="dashboard-section-title">Top Sell Signals</h6>
                      <div className="card shadow dashboard-card">
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th>TICKER</th>
                                <th>CAPE VALUE</th>
                                <th>F-SCORE</th>
                                <th>SIGNAL</th>
                              </tr>
                            </thead>
                            <tbody>
                              {allSellSignals.length > 0 ? (
                                allSellSignals.map((signal) => (
                                  <tr key={signal.ticker}>
                                    <td>
                                      <span 
                                        className="text-primary stock-link"
                                        onClick={() => handleViewStock(signal.ticker)}
                                      >
                                        {signal.ticker}
                                      </span>
                                    </td>
                                    <td>{signal.cape !== undefined ? signal.cape.toFixed(2) : 'N/A'}</td>
                                    <td>
                                      {signal.fScore && typeof signal.fScore === 'object' && 'score' in signal.fScore 
                                        ? `${signal.fScore.score}/9` 
                                        : signal.f_score ? `${signal.f_score}/9` : 'N/A'}
                                    </td>
                                    <td>
                                      {renderSignalBadge(signal.finalSignal as SignalAction)}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={4} className="text-center">No sell signals available</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Visualization Tab Content */}
                <div className={`tab-pane fade ${activeTab === 'visualization' ? 'show active' : ''}`}>
                  <SimpleRatioChart />
                </div>

                {/* Portfolio Management Tab Content */}
                <div className={`tab-pane fade ${activeTab === 'portfolio' ? 'show active' : ''}`}>
                  <div className="mt-4">
                    <PortfolioManager onViewStock={handleViewStock} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleDashboard; 